import { PRIOR_AUTH_REQUIREMENT_CODESYSTEM } from '@bariendo/lib/constants';
import { PrepItem } from '@bariendo/lib/payment-status';
import { readPrepItems } from '@bariendo/lib/prior-auth-prep';
import { Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useState } from 'react';
import { canSearchResourceType } from '@/services/permissions';
import { groupByPatient, searchAllByPatients } from '../services/pacQueueHydration';

export interface PrepItemsState {
  /** Each patient's checklist, from their newest prep set (BAR-3098). */
  itemsByPatient: Map<string, PrepItem[]>;
  loading: boolean;
}

const EMPTY: PrepItemsState = { itemsByPatient: new Map(), loading: false };

/**
 * The page's prior auth prep checklists, one search per chunk of patients. Nothing is read for a
 * role whose policy can't search Task; a failed read leaves the cells empty, not the table.
 */
export function usePrepItems(patientRefs: string[]): PrepItemsState {
  const medplum = useMedplum();
  const key = useMemo(() => [...patientRefs].sort().join(','), [patientRefs]);
  const [state, setState] = useState<PrepItemsState>(EMPTY);

  useEffect(() => {
    const refs = key ? key.split(',') : [];
    if (refs.length === 0 || !canSearchResourceType(medplum, 'Task')) {
      setState(EMPTY);
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true }));
    searchAllByPatients<Task>(medplum, 'Task', 'subject', refs, {
      code: `${PRIOR_AUTH_REQUIREMENT_CODESYSTEM}|`,
      _count: '1000',
    })
      .then((tasks) => {
        if (cancelled) return;
        const byPatient = groupByPatient(tasks, (task) => task.for?.reference);
        setState({
          itemsByPatient: new Map(
            [...byPatient].map(([patient, patientTasks]) => [patient, readPrepItems(patientTasks)])
          ),
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState(EMPTY);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, key]);

  return state;
}
